import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  getSubprofileStats,
  SubProfileAccountStats,
  SubProfileSummary,
} from './core/_requests'

const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const SubprofilesPage: React.FC = () => {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<SubProfileSummary | null>(null)
  const [accounts, setAccounts] = useState<SubProfileAccountStats[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await getSubprofileStats()
        if (!cancelled) {
          setSummary(res.data.summary)
          setAccounts(res.data.accounts || [])
        }
      } catch (error) {
        console.error('Failed to load subprofile stats:', error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const filteredAccounts = accounts.filter((acc) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      (acc.email || '').toLowerCase().includes(q) ||
      (acc.name || '').toLowerCase().includes(q) ||
      (acc.company_name || '').toLowerCase().includes(q) ||
      acc.profiles.some((p) => p.name.toLowerCase().includes(q))
    )
  })

  const statsData = [
    {
      label: 'סה"כ תתי דפים',
      value: summary?.total_sub_profiles || 0,
      icon: 'bi-layers-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'תתי דפים פעילים',
      value: summary?.active_sub_profiles || 0,
      icon: 'bi-lightning-charge-fill',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
      label: 'תתי דפים לא פעילים',
      value: summary?.inactive_sub_profiles || 0,
      icon: 'bi-pause-circle-fill',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      label: 'חשבונות עם תתי דפים',
      value: summary?.accounts_with_active || 0,
      icon: 'bi-people-fill',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
  ]

  return (
    <div style={{padding: 0}}>
      {/* Page Header */}
      <div style={{marginBottom: '32px'}}>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: colors.textPrimary,
            margin: 0,
          }}
        >
          תתי דפים (Sub-Profiles)
        </h1>
        <p style={{color: colors.textSecondary, marginTop: '8px', fontSize: '14px'}}>
          סטטיסטיקות תתי דפים פעילים לפי חשבון
        </p>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {statsData.map((stat, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '20px',
              transition: 'all 0.2s ease',
              transform: hoveredCard === i ? 'translateY(-2px)' : 'none',
              border: `1px solid ${hoveredCard === i ? colors.borderColor : 'transparent'}`,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}
            >
              <i className={`bi ${stat.icon}`} style={{fontSize: '20px', color: '#fff'}}></i>
            </div>
            <div
              style={{
                fontSize: '28px',
                fontWeight: 700,
                color: colors.textPrimary,
                marginBottom: '4px',
              }}
            >
              {stat.value.toLocaleString()}
            </div>
            <div style={{fontSize: '13px', color: colors.textSecondary}}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Extra summary row */}
      {summary && (
        <div
          style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '20px',
            padding: '12px 16px',
            background: colors.bgTertiary,
            borderRadius: '8px',
            fontSize: '13px',
            color: colors.textSecondary,
          }}
        >
          <span>
            דפי אב (פרופילים ראשיים):{' '}
            <strong style={{color: colors.textPrimary}}>
              {summary.total_parent_profiles.toLocaleString()}
            </strong>
          </span>
          <span>
            יחס תתי דפים פעילים:{' '}
            <strong style={{color: colors.textPrimary}}>
              {summary.total_sub_profiles > 0
                ? ((summary.active_sub_profiles / summary.total_sub_profiles) * 100).toFixed(1)
                : 0}
              %
            </strong>
          </span>
        </div>
      )}

      {/* Search Bar */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '20px',
          alignItems: 'center',
          background: colors.bgTertiary,
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <div style={{flex: 1, position: 'relative'}}>
          <i
            className='bi bi-search'
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.textMuted,
              fontSize: '16px',
            }}
          ></i>
          <input
            type='text'
            placeholder='חיפוש לפי שם חשבון, אימייל, שם פרופיל...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 44px 12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: colors.textPrimary,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
        <div style={{fontSize: '13px', color: colors.textSecondary}}>
          {filteredAccounts.length} חשבונות
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: colors.bgTertiary,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2.5fr 1fr 1fr 0.5fr',
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          {['חשבון', 'תתי דפים פעילים', 'אונליין', ''].map((header, i) => (
            <div
              key={i}
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: colors.textMuted,
              }}
            >
              {header}
            </div>
          ))}
        </div>

        {/* Table Body */}
        {loading ? (
          <div
            style={{
              padding: '60px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${colors.bgHover}`,
                borderTopColor: colors.accentPrimary,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            ></div>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div style={{padding: '60px', textAlign: 'center'}}>
            <i
              className='bi bi-inbox'
              style={{fontSize: '48px', color: colors.textMuted, marginBottom: '16px', display: 'block'}}
            ></i>
            <div style={{fontSize: '18px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px'}}>
              לא נמצאו חשבונות
            </div>
            <div style={{fontSize: '14px', color: colors.textSecondary}}>נסה לשנות את פרמטרי החיפוש</div>
          </div>
        ) : (
          filteredAccounts.map((account) => {
            const displayName = account.name?.trim() || account.email || 'Unknown'
            const isExpanded = expandedAccount === account.account_id
            const onlineCount = account.profiles.filter((p) => p.is_online).length

            return (
              <React.Fragment key={account.account_id}>
                <div
                  onClick={() =>
                    setExpandedAccount(isExpanded ? null : account.account_id)
                  }
                  onMouseEnter={() => setHoveredRow(account.account_id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2.5fr 1fr 1fr 0.5fr',
                    padding: '16px 24px',
                    alignItems: 'center',
                    borderBottom: `1px solid rgba(0,0,0,0.05)`,
                    cursor: 'pointer',
                    background: isExpanded
                      ? 'rgba(54, 153, 255, 0.06)'
                      : hoveredRow === account.account_id
                      ? colors.bgHover
                      : 'transparent',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {/* Account Info */}
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#fff',
                      }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{fontWeight: 600, color: colors.textPrimary, fontSize: '14px'}}>
                        {displayName}
                      </div>
                      <div style={{fontSize: '12px', color: colors.textSecondary, marginTop: '2px'}}>
                        {account.email}
                      </div>
                      {account.company_name && (
                        <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '1px'}}>
                          {account.company_name}
                        </div>
                      )}
                    </div>
                    {account.is_locked && (
                      <span
                        style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: 'rgba(246, 78, 96, 0.15)',
                          color: colors.accentDanger,
                          fontWeight: 600,
                        }}
                      >
                        נעול
                      </span>
                    )}
                  </div>

                  {/* Active Sub-Profiles Count */}
                  <div>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 700,
                        background: 'rgba(102, 126, 234, 0.15)',
                        color: '#667eea',
                      }}
                    >
                      {account.active_sub_profiles}
                    </span>
                  </div>

                  {/* Online Count */}
                  <div>
                    {onlineCount > 0 ? (
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: 'rgba(27, 197, 189, 0.15)',
                          color: colors.accentSuccess,
                        }}
                      >
                        {onlineCount} / {account.active_sub_profiles}
                      </span>
                    ) : (
                      <span style={{color: colors.textMuted, fontSize: '12px'}}>0 / {account.active_sub_profiles}</span>
                    )}
                  </div>

                  {/* Expand Arrow */}
                  <div style={{textAlign: 'center'}}>
                    <i
                      className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
                      style={{color: colors.textMuted, fontSize: '14px'}}
                    ></i>
                  </div>
                </div>

                {/* Expanded Profile List */}
                {isExpanded && (
                  <div
                    style={{
                      padding: '0 24px 16px 24px',
                      background: 'rgba(54, 153, 255, 0.03)',
                      borderBottom: `1px solid rgba(0,0,0,0.05)`,
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '10px',
                        paddingTop: '12px',
                      }}
                    >
                      {account.profiles.map((profile) => (
                        <div
                          key={profile._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            background: colors.bgSecondary,
                            borderRadius: '8px',
                            border: `1px solid ${colors.borderColor}`,
                          }}
                        >
                          {/* Online indicator */}
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: profile.is_online ? colors.accentSuccess : colors.textMuted,
                              flexShrink: 0,
                            }}
                          ></div>
                          <div style={{flex: 1, minWidth: 0}}>
                            <div
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: colors.textPrimary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {profile.name}
                            </div>
                            {profile.facebook_id && (
                              <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '2px'}}>
                                {profile.facebook_id}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: '11px',
                              color: colors.textSecondary,
                              flexShrink: 0,
                            }}
                          >
                            {profile.posts_per_day}/יום
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Link to account detail */}
                    <div style={{marginTop: '12px', textAlign: 'left'}}>
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/admin/accounts/${account.account_id}`)
                        }}
                        style={{
                          fontSize: '12px',
                          color: colors.accentPrimary,
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        צפה בחשבון &larr;
                      </span>
                    </div>
                  </div>
                )}
              </React.Fragment>
            )
          })
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default SubprofilesPage
